import { ApiError } from '@/shared/api/http';

/* Why a read failed, in the reader's terms, derived from the status rather than assumed.
 *
 * **This exists because a 403 was being reported as a connectivity failure.** The Connections screen
 * rendered a hardcoded hint — *"Lumiq could not reach its own API for this list. Nothing is wrong
 * with your sources — retry, and if it persists check that the platform is running"* — for every
 * error, and an approver who does not hold `catalog.connection:read` got exactly that, above a
 * technical detail reading `read on catalog.connection was denied`. Three things were wrong at once:
 * the message asserted a cause the evidence on the same panel contradicted, it sent the reader to
 * check a platform that was running perfectly, and it offered a Retry that could only fail again.
 *
 * The status is the one thing that distinguishes these, and the client already has it: `ApiError`
 * carries it. So the guidance is *derived*, and a screen cannot accidentally attribute a denial to
 * an outage by writing one sentence for every failure.
 *
 * `retryable` is part of the answer rather than a separate judgement. A denial and a not-found do
 * not become true on a second attempt, and a control that cannot work is worse than a missing one —
 * the same rule this codebase applies to a Cancel with nowhere to go.
 */

export type FailureKind =
  /** 401. The token is gone or expired; `http.ts` has already cleared it. */
  | 'unauthenticated'
  /** 403. The server understood, and this role may not. */
  | 'denied'
  /** 404. Asked for something that is not there. */
  | 'absent'
  /** 429. Refused for rate rather than for right — the one refusal that *is* worth retrying. */
  | 'throttled'
  /** 5xx. The platform failed at something it should have managed. */
  | 'server'
  /** No status at all: a network failure, a DNS failure, a guard rejecting the payload shape. */
  | 'unreachable';

export interface FailureDescription {
  readonly kind: FailureKind;
  /** An ICU key. Takes `{subject}` for every kind, so a caller always passes one. */
  readonly guidanceKey: string;
  /** Whether trying again could plausibly succeed. */
  readonly retryable: boolean;
}

const GUIDANCE: Readonly<Record<FailureKind, string>> = {
  unauthenticated: 'failure.unauthenticated',
  denied: 'failure.denied',
  absent: 'failure.absent',
  throttled: 'failure.throttled',
  server: 'failure.server',
  unreachable: 'failure.unreachable',
};

/* Only these two are worth another attempt. A 429 is a refusal about *rate* and clears by itself; a
 * 5xx may be transient. A 401 clears by signing in, not by repeating, and `http.ts` has already
 * dropped the token by the time this is called. */
const RETRYABLE: Readonly<Record<FailureKind, boolean>> = {
  unauthenticated: false,
  denied: false,
  absent: false,
  throttled: true,
  server: true,
  unreachable: true,
};

export function failureKind(error: unknown): FailureKind {
  /* **Not `instanceof ApiError` alone.** A guard rejecting a response shape, a network failure and an
   * aborted request all arrive as plain `Error`s, and every one of them is genuinely "we could not
   * get an answer" — which is what `unreachable` means. Treating an unknown throw as a *server*
   * fault would blame the platform for a client-side refusal. */
  if (!(error instanceof ApiError)) return 'unreachable';
  if (error.status === 401) return 'unauthenticated';
  if (error.status === 403) return 'denied';
  if (error.status === 404) return 'absent';
  if (error.status === 429) return 'throttled';
  /* Anything from 500 up is ours. Deliberately a floor rather than a list: a 502 from the edge and a
   * 503 from a refusing capability are both the platform failing, and enumerating them would let the
   * next one fall through to a message about the network. */
  if (error.status >= 500) return 'server';
  /* A 4xx this model has no branch for — 409, 422, 400. These are refusals *about the request*, and
   * a read has no body to be wrong, so reaching here means a screen sent something it should not
   * have. `server` rather than `unreachable`, because the platform is reachable and the honest
   * reading is that something on our side is wrong. */
  return 'server';
}

export function describeFailure(error: unknown): FailureDescription {
  const kind = failureKind(error);
  return { kind, guidanceKey: GUIDANCE[kind], retryable: RETRYABLE[kind] };
}
